import { StemType } from '@arpeggi/shared'
import { el } from '@elemaudio/core'
import AudioClipPlayer, { AudioClipPlayerProps } from '../modules/AudioClipPlayer'
import DrumSampler, { DrumSamplerProps } from '../modules/DrumSampler'
import PolySampler, { PolySamplerProps } from '../modules/PolySampler'
import { DRUM_GAIN_BOOST, GAIN_BOOST, GLOBAL_GAIN_BOOST, MINUS_3DB } from './AudioConstants'
import { tempoToIntervalMs, treeAdd } from './ElementaryUtils'

/** A portable version of AudioEngine.createGraph(), this function should be able to be used
 * anywhere that elementary can be run, as long as it has access to an arpeggi song json object
 */
export const createGraph = (
  stems: SerializableStem[],
  transport,
  tempo: number,
  currentGain: number,
  repeat,
  // TODO this needs to be consolidated into a single type.
  sampleMaps: (
    | /* Drums */ string[]
    | /* Instruments: */ SerializableSampleMap
    | /* Clips: */ any[]
  )[],
  playheadStartPositionTicks: number,
  soloTrackIndex: number = -1,
  mode = 'core',
  storeType: string | null = null,
  metronomeValues: {
    value: number
    metronome: (transport, ppqnSignal, elapsedTicks) => any
  } = {
    value: 0,
    metronome: () => {}
  }
) => {
  const clockInterval = el.const({
    key: 'clockInterval',
    value: tempoToIntervalMs(tempo, 48)
  })

  const clock48n = el.le(el.phasor(el.div(1000, clockInterval), transport), 0.5)

  // Playhead reset
  const repeatEngaged = el.const({ key: 'repeat', value: repeat.repeatSection ? 1 : 0 })
  const repeatImpulseRising = el.ge(el.sub(repeatEngaged, el.z(repeatEngaged)), 0)
  const repeatImpulseFalling = el.le(el.sub(repeatEngaged, el.z(repeatEngaged)), 0)
  const ppqnSignal = el.const({ key: 'ppqn', value: playheadStartPositionTicks })
  const ppqnChange = el.abs(el.sub(ppqnSignal, el.z(ppqnSignal)))
  const resetClock = el.meter(
    el.sub(transport, repeatImpulseRising, repeatImpulseFalling, ppqnChange)
  )

  // This counts the number of ticks elapsed since we last started the transport
  // It will reset to 0 every time we pause/stop and play again.
  const elapsedTicks = el.accum(el.ge(el.sub(clock48n, el.z(clock48n)), 0), resetClock)

  // So the thing we snapshot is our elapsed ticks
  const snapshot = el.snapshot({ key: 'playhead', name: 'playhead' }, el.train(60), elapsedTicks)

  // Playhead position in 48n ticks
  const ppqn = playheadStartPositionTicks

  // Base clocks
  const baseClock = el.mul(transport, clock48n)

  // Main render bit
  let studioGraph: any[] = stems
    .map((stem: SerializableStem, i: number) => {
      const stemType = stem.stemType
      const isSolo = i === soloTrackIndex
      const gain = el.const({
        key: `stem-gain-${stem.uniqueId}`,
        value:
          !isSolo && soloTrackIndex !== -1
            ? 0
            : stem.stemSettings.stemGain *
              (stemType === StemType.DRUMS ? DRUM_GAIN_BOOST : GAIN_BOOST)
      })

      if (stemType === StemType.DRUMS) {
        const drumStem = stem as SerializableDrumStem
        if (drumStem.drumSamples.length === 0) return
        const sampleMap = sampleMaps[i]

        let props = {
          key: stem.uniqueId,
          sampleMap: sampleMap as string[],
          stem: drumStem,
          ppqn,
          repeat
        }
        const propsL = Object.assign({}, props, { channel: 0 })
        const propsR = Object.assign({}, props, { channel: 1 })
        return [
          el.mul(
            MINUS_3DB,
            el.sm(gain),
            DrumSampler(propsL as DrumSamplerProps, baseClock, resetClock)
          ),
          el.mul(
            MINUS_3DB,
            el.sm(gain),
            DrumSampler(propsR as DrumSamplerProps, baseClock, resetClock)
          )
        ]
      } else if (stemType === StemType.INSTRUMENT) {
        const instrumentStem = stem as SerializableInstrumentStem
        // Prepare sample
        const sampleMap = sampleMaps[i] as SerializableSampleMap

        let props = {
          key: stem.uniqueId,
          stem: instrumentStem,
          sampleMap,
          ppqn,
          repeat
        }
        const propsL = Object.assign({}, props, { channel: 0 })
        const propsR = Object.assign({}, props, { channel: 1 })
        return [
          el.mul(
            MINUS_3DB,
            el.sm(gain),
            PolySampler(propsL as PolySamplerProps, baseClock, resetClock)
          ),
          el.mul(
            MINUS_3DB,
            el.sm(gain),
            PolySampler(propsR as PolySamplerProps, baseClock, resetClock)
          )
        ]
      } else if (stemType === StemType.AUDIO) {
        const audioStem = stem as SerializableAudioStem
        // Prepare clip samples
        const clips = audioStem.clips
        if (clips.length === 0) return

        const clipsWithAudio = sampleMaps[i] as SerializableSampleMap[]

        const propsBase = {
          index: stem.uniqueId,
          tempo: tempo,
          stem: audioStem,
          mode,
          ppqn,
          repeat,
          clipsWithAudio
        }
        const propsL: AudioClipPlayerProps = { ...propsBase, channel: 0 }
        const propsR: AudioClipPlayerProps = { ...propsBase, channel: 1 }
        return [
          el.mul(MINUS_3DB, el.sm(gain), AudioClipPlayer(propsL, baseClock, resetClock)),
          el.mul(MINUS_3DB, el.sm(gain), AudioClipPlayer(propsR, baseClock, resetClock))
        ]
      }
    })
    .filter((item) => item)

  if (stems.length === 0) studioGraph = [0, 0]

  let [graphOutLeft, graphOutRight]: [any, any] = studioGraph.reduce(
    (acc, stem) => {
      if (Array.isArray(stem) && stem.length > 1) {
        return [
          [...acc[0], stem[0]],
          [...acc[1], stem[1]]
        ]
      } else {
        return [
          [...acc[0], stem],
          [...acc[1], stem]
        ]
      }
    },
    [[], []]
  )

  if (storeType === 'studio' && mode === 'core') {
    // Create and push metronome node to the graph
    const metronome = el.mul(
      el.const({ key: 'metronome', value: metronomeValues.value }),
      metronomeValues.metronome(transport, ppqnSignal, elapsedTicks)
    )
    graphOutLeft.push(metronome)
    graphOutRight.push(metronome)
  }
  // Apply global gain
  const globalGain = el.const({
    key: 'globalGain',
    value: currentGain * GLOBAL_GAIN_BOOST
  })

  // Create graph outs with treeAdd
  graphOutLeft = el.mul(el.sm(globalGain), treeAdd(graphOutLeft))
  graphOutRight = el.mul(el.sm(globalGain), treeAdd(graphOutRight))

  // Add in the snapshot listener
  graphOutRight = el.add(el.mul(0, snapshot), graphOutRight)

  return [graphOutLeft, graphOutRight]
}

interface SerializableStem {
  stemType: string // one of the values in StemTypes
  uniqueId: string
  stemSettings: { stemGain: number }
}

export interface SerializableInstrumentStem extends SerializableStem {
  melodicSample: any
}

export interface SerializableDrumStem extends SerializableStem {
  drumSamples: any[]
  loops: any[]
}

export interface SerializableAudioStem extends SerializableStem {
  clips: any[]
  startInComposition48n: number
}

export interface SerializableSampleMap {
  path: string
  audioData: any[]
  sampleWithEffects: any
}
